import DataMap from 'assets/components/dataMap/DataMap';
import useForm from 'assets/components/form/hooks/Form';
import { getLocales } from 'assets/locales/Locale';
import { compact } from 'lodash';
import ClaimTypeDocument from 'models/claims/claimTypeDocument/Model.ClaimTypeDocument';
import { Fragment, useMemo } from 'react';
import { usePublicCreateClaimProvider } from '../Hooks';
import Theme from 'assets/themes/Theme.Common';
import FileInputSimple from 'assets/components/inputs/file/FileInputSimple';
import MimeType from 'assets/utils/file/mimeType/MimeType';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import TextInput from 'assets/components/inputs/text/TextInput';
import MultilineTextInput from 'assets/components/inputs/multilineText/MultilineTextInput';
import Box from 'assets/components/box/Box';
import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import Icon from 'assets/components/icon/Icon';

const allowedMimeTypes = [MimeType.imageJPG, MimeType.imageJPEG, MimeType.imagePNG, MimeType.appPDF];

function OneDocForm(props: { claimTypeDocument: ClaimTypeDocument; claimJobIndex: number }) {
  const { lang } = getLocales();
  const { form } = usePublicCreateClaimProvider();

  const claimJobs = form.data?.claim?.claimJobs || [];
  const claimJob = claimJobs[props.claimJobIndex];

  const alreadySubmittedDocs = claimJob?.claimJobDocuments?.filter(
    (cjd) => cjd.claimTypeDocumentId === props.claimTypeDocument.id
  );
  const isAlreadySubmitted = !!alreadySubmittedDocs.length;

  const docForm = useForm<{
    claimTypeDocumentId: number;
    documents: Array<Utils.FormData<Omit<Model.IClaimJobDocument, 'claimTypeDocumentId'>>>;
  }>({
    default: {
      claimTypeDocumentId: props.claimTypeDocument.id,
      documents: [],
    },
    onSubmit: async ({ claimTypeDocumentId, documents }) => {
      for (const document of documents) {
        const updatedClaimJobs = [...claimJobs];
        const currentJob = { ...updatedClaimJobs[props.claimJobIndex] };
        currentJob.claimJobDocuments = [...(currentJob.claimJobDocuments ?? []), { claimTypeDocumentId, ...document }];
        updatedClaimJobs[props.claimJobIndex] = currentJob;
        form.merge({ claim: { claimJobs: updatedClaimJobs } });
      }
      docForm.reload();
    },
    validation: (data, errors) => {
      if (data.documents?.some((it) => !it.name)) errors.documents = { name: lang.required };
      if (props.claimTypeDocument.required && !isAlreadySubmitted && data.documents?.some((it) => !it.formFile))
        errors.documents = { formFile: { _objectError: lang.required } };
    },
  });

  const uploadedFiles = useMemo(
    () =>
      docForm.data?.documents?.map((doc) => ({
        original: doc.formFile,
        type: doc.formFile.type as Utils.File.MimeType,
        dataUrl: window.URL.createObjectURL(doc.formFile),
        name: doc.name,
        notes: doc.notes,
      })),
    [docForm.data.documents]
  );

  const uploadedCount = (uploadedFiles.length ?? 0) + (alreadySubmittedDocs?.length ?? 0);
  const uploadLimit = props.claimTypeDocument.allowMultiple ? 5 : 1;
  const allowMoreUploads = uploadedCount < uploadLimit;

  function uploadFiles(files: File[]) {
    docForm.update({
      documents: [
        ...(docForm.data.documents ?? []),
        ...files.slice(0, uploadLimit - uploadedCount).map((file) => ({
          formFile: file,
          name: file.name,
          approved: !props.claimTypeDocument.approvalRequired,
        })),
      ],
    });
  }
  return (
    <>
      <br />
      <FileInputSimple
        name="files"
        label={props.claimTypeDocument.name}
        value={uploadedFiles}
        onChange={({ files }) => {
          uploadFiles(compact(files.map((it) => it.original)));
        }}
        fileLimit={uploadLimit}
        disabled={!allowMoreUploads}
        allowedMimeTypes={allowedMimeTypes}
      />

      {!!uploadedFiles.length && (
        <>
          <br />
          <FormWrapper style={{ padding: '0 !important' }}>
            <DataMap
              data={uploadedFiles}
              render={({ data: uploadedFile, index: fileIndex }) => (
                <Fragment key={`${uploadedFile.name}-${fileIndex}`}>
                  <TextInput
                    name="name"
                    label={lang.name}
                    error={!uploadedFile.name ? lang.required : undefined}
                    value={uploadedFile.name ?? ''}
                    style={{ width: '100%' }}
                    onChange={({ name }) => {
                      docForm.data.documents[fileIndex] = {
                        ...docForm.data.documents[fileIndex],
                        name,
                      };

                      docForm.update({
                        documents: [...docForm.data.documents],
                      });
                    }}
                  />
                  <MultilineTextInput
                    name="notes"
                    label={lang.notes}
                    value={uploadedFile.notes ?? ''}
                    style={{ width: '100%' }}
                    onChange={({ notes }) => {
                      docForm.data.documents[fileIndex] = {
                        ...docForm.data.documents[fileIndex],
                        notes,
                      };

                      docForm.update({
                        documents: [...docForm.data.documents],
                      });
                    }}
                  />
                </Fragment>
              )}
            />
          </FormWrapper>
          <Box style={{ display: 'flex', gap: Theme.Size.M }}>
            <Button class={ThemeButtonCircle} onClick={docForm.reload}>
              {lang.cancel}
            </Button>
            <Button
              class={ThemeButtonCircle}
              onClick={docForm.submit}
              disabled={docForm.hasErrors}
              active={!docForm.hasErrors}
            >
              {lang.submit}
            </Button>
          </Box>
        </>
      )}
      <br />
      <br />
    </>
  );
}

type PublicCreateClaimJobDocumentsProps = {
  claimJob: Utils.FormData<Module.Claims.Claim.Submit.ClaimJobForUpdate>;
  claimJobIndex: number;
};

export default function PublicCreateClaimJobDocuments({ claimJob, claimJobIndex }: PublicCreateClaimJobDocumentsProps) {
  const { lang } = getLocales();
  const { form, uploadableDocuments } = usePublicCreateClaimProvider();

  if (!uploadableDocuments?.length || !form?.data?.claim?.claimJobs?.length) return null;
  const uploadedDocIds = new Set(claimJob?.claimJobDocuments?.map((doc) => doc.claimTypeDocumentId));
  const missingRequiredDocs = uploadableDocuments.filter(
    (doc) => doc.claimTypeId === claimJob?.claimTypeId && doc.required && !uploadedDocIds.has(doc.id)
  );

  const areThereUploadableDocuments = form.data?.claim?.claimJobs?.some((job) =>
    uploadableDocuments.some((doc) => doc.claimTypeId === job.claimTypeId)
  );
  return !areThereUploadableDocuments ? null : (
    <>
      {missingRequiredDocs.length > 0 && (
        <p
          style={{
            backgroundColor: Theme.Color.error,
            color: 'white',
            padding: `${Theme.Size.M} ${Theme.Size.L}`,
            fontWeight: 'bold',
          }}
        >
          {lang.requiredFilesForTheFollowingDocs}:
          <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
            <div style={{ marginLeft: Theme.Size.M }}>
              {missingRequiredDocs.map((doc) => (
                <li key={doc.id}>{doc.name}</li>
              ))}
            </div>
          </ul>
        </p>
      )}
      <DataMap
        data={uploadableDocuments.filter((it) => it.claimTypeId === claimJob?.claimTypeId)}
        render={({ data: doc }) => (
          <OneDocForm key={`${claimJobIndex}-${doc.id}`} claimTypeDocument={doc} claimJobIndex={claimJobIndex} />
        )}
      />
      <table className={ThemeTableSimple}>
        <thead>
          <tr>
            <th>{lang.fileName}</th>
            <th>{lang.documentName}</th>
            <th>{lang.notes}</th>
            <th data-actions>{lang.actions}</th>
          </tr>
        </thead>
        <tbody>
          {!claimJob?.claimJobDocuments?.length && (
            <tr>
              <td colSpan={7}>
                <Icon class="fas-inbox" style={{ fontSize: Theme.FontSize.XXL }} />
                <br />
                {lang.noInfo}
              </td>
            </tr>
          )}
          <DataMap
            data={claimJob?.claimJobDocuments ?? []}
            render={({ data: document, index: docIdx }) => (
              <tr key={docIdx}>
                <td>
                  <strong>{document.name}</strong>
                </td>
                <td>
                  {
                    uploadableDocuments.find(
                      (ud) => ud.id === document.claimTypeDocumentId && ud.claimTypeId === claimJob?.claimTypeId
                    )?.name
                  }
                </td>
                <td>{document.notes}</td>
                <td>
                  <Button
                    class={ThemeButtonCircle}
                    onClick={() => {
                      const updatedJobs = [...(form.data?.claim?.claimJobs ?? [])];
                      const updatedDocuments = claimJob.claimJobDocuments?.filter((_, i) => i !== docIdx);
                      updatedJobs[claimJobIndex].claimJobDocuments = updatedDocuments;
                      form.merge({ claim: { claimJobs: updatedJobs } });
                    }}
                  >
                    {lang.delete}
                  </Button>
                </td>
              </tr>
            )}
          />
        </tbody>
      </table>
    </>
  );
}
